import React from 'react';
import { CheckCircle2, Crown, Ticket, ExternalLink, Armchair } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ORANGE_STREET_TIERS,
  ORANGE_STREET_VIP_TABLES,
  ORANGE_STREET_EVENTBRITE_URL,
  orangeStreetTablesAvailable,
} from '@/config/promotions';

/**
 * Display-only ticket pricing for Orange Street: GA-style tiers plus the VIP
 * table options (with shared 24-total / 5-sold availability). Purchasing is
 * Eventbrite-only, so every CTA links out to the Eventbrite event.
 */
export const OrangeStreetTiers = () => {
  const tablesAvailable = orangeStreetTablesAvailable();
  const { totalTables, soldTables, options } = ORANGE_STREET_VIP_TABLES;
  const soldOut = tablesAvailable <= 0;

  const EventbriteButton = ({ label = 'Get Tickets on Eventbrite', variant = 'premium', size = 'lg', full = true }) => (
    <a
      href={ORANGE_STREET_EVENTBRITE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={full ? 'block' : 'inline-block'}
      aria-label="Get Orange Street tickets on Eventbrite (opens in a new tab)"
    >
      <Button variant={variant} size={size} className={full ? 'w-full' : ''}>
        <Ticket className="w-5 h-5 mr-2" />
        {label}
        <ExternalLink className="w-4 h-4 ml-2" />
      </Button>
    </a>
  );

  return (
    <div className="space-y-5">
      {/* General admission tiers */}
      <div>
        <h3 className="text-lg font-display font-bold mb-3">General Admission Tiers</h3>
        <div className="space-y-2">
          {ORANGE_STREET_TIERS.map((tier) => (
            <div
              key={tier.id}
              className="flex items-start justify-between gap-3 rounded-lg border border-border bg-muted/40 p-3"
            >
              <div>
                <p className="font-semibold">{tier.name}</p>
                <p className="text-xs text-muted-foreground">{tier.perks.join(' • ')}</p>
              </div>
              <p className="text-xl font-bold text-primary whitespace-nowrap">${tier.price}</p>
            </div>
          ))}
        </div>
      </div>

      {/* VIP tables */}
      <div>
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="text-lg font-display font-bold flex items-center gap-2">
            <Armchair className="w-5 h-5 text-primary" />
            VIP Tables
          </h3>
          <Badge
            variant="outline"
            className={soldOut ? 'bg-destructive/10 text-destructive border-destructive/30' : 'bg-primary/10 text-primary border-primary/30'}
          >
            {soldOut ? 'Sold Out' : `${tablesAvailable} of ${totalTables} available`}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          {soldTables} of {totalTables} tables sold • includes bottle service
        </p>
        <div className="space-y-2">
          {options.map((opt, i) => (
            <div
              key={opt.id}
              className="rounded-lg border border-primary/30 bg-primary/5 p-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold flex items-center gap-1.5">
                    {i === options.length - 1 && <Crown className="w-4 h-4 text-primary" />}
                    {opt.name}
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {opt.perks.map((perk) => (
                      <li key={perk} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <CheckCircle2 className="w-3 h-3 text-primary flex-shrink-0" />
                        {perk}
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="text-xl font-bold text-primary whitespace-nowrap">${opt.price.toLocaleString()}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <EventbriteButton />
      <p className="text-center text-[11px] text-muted-foreground">
        Tickets are sold and fulfilled by Eventbrite. Prices and availability are confirmed at checkout.
      </p>
    </div>
  );
};

export default OrangeStreetTiers;
